import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../config/api';
import { useAuth } from './AuthContext';
import AuthService from '../services/authService';
import UserPreferencesService from '../services/userPreferencesService';
import { getUserStorage, setUserStorage, removeUserStorage, migrateToUserStorage } from '../utils/userStorage';

// Keys that need user-specific storage
const USER_STORAGE_KEYS = ['dismissedHints', 'hasCompletedOnboarding'];

// Create the Onboarding Context
const OnboardingContext = createContext();

// Current versions - increment when onboarding flow changes significantly
//
// v2 = the /welcome first-run flow (home location → follows → quiz). Bumped
// from v1 (the standalone WouldYouRather quiz) because the gate uses this to
// decide who still needs the current flow: a user who completed or skipped v1
// has never seen v2, so they're offered it once if they're missing what it
// captures. Recorded on both completion and skip.
const ONBOARDING_VERSION = 'v2';

// How long an account counts as new: the Home "Getting started" row shows
// only in this window (until it's finished or dismissed).
const NEW_ACCOUNT_DAYS = 30;

// users.created_at is SQLite's 'YYYY-MM-DD HH:MM:SS', in UTC with no marker.
function parseUtc(value) {
  if (!value) return null;
  const iso = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Custom hook to use the onboarding context
 */
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

/**
 * Onboarding Provider Component
 *
 * Manages all onboarding and feature discovery state:
 * - Onboarding quiz completion
 * - Feature hint dismissals
 * - User milestone tracking
 */
export const OnboardingProvider = ({ children }) => {
  // Get auth state from AuthContext
  const { currentUser, authLoading } = useAuth();

  // Track the previous user ID to detect user changes
  const prevUserIdRef = useRef(null);

  // Core onboarding state
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null); // null = loading
  const [onboardingStep, setOnboardingStep] = useState(0);

  // What the first-run flow captures, straight from the server. Not cached in
  // localStorage — a stale "already has a location" would skip the flow for
  // someone who needs it, and the value is free (it rides the state fetch).
  const [hasHomeLocation, setHasHomeLocation] = useState(null);
  const [followCount, setFollowCount] = useState(null);
  // Which onboarding version this user last completed or skipped (null = none).
  const [onboardingVersion, setOnboardingVersion] = useState(null);
  // users.created_at, from the same fetch (null = not loaded / unknown)
  const [accountCreatedAt, setAccountCreatedAt] = useState(null);

  // Feature hints state (localStorage cached, DB backed)
  const [dismissedHints, setDismissedHints] = useState(new Set());

  // Milestones state - all keys must match server/routes/onboardingRoutes.js
  const [milestones, setMilestones] = useState({
    // Action milestones
    firstSearch: false,
    firstSave: false,
    firstTripCreated: false,
    firstActivityScheduled: false,
    firstCollectionCreated: false,
    firstTripShared: false,
    firstSocialInteraction: false,
    firstPhotoUploaded: false,
    // Page visit milestones (for tour tracking)
    visitedDiscover: false,
    visitedPlanner: false,
    visitedSavedItems: false,
    visitedTripPlanner: false,
    visitedSearchResults: false,
    visitedProfile: false,
    visitedActivityDetail: false,
    visitedItinerary: false,
    // Feature tour milestones
    completedSettingsTour: false
  });

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // ============================================================
  // INITIALIZATION
  // ============================================================

  /**
   * Load onboarding state from localStorage (immediate) and database (authoritative)
   *
   * `silent` re-reads the server without going back to the loading state, for
   * a refresh after the user changed something: flipping isLoading would blank
   * every surface that waits on it (the Home checklist, the tab-bar dots).
   */
  const loadOnboardingState = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);

    try {
      // Migrate any old non-user-specific keys to user-specific keys
      migrateToUserStorage(USER_STORAGE_KEYS);

      // 1. Load from user-specific localStorage for immediate UI (optimistic)
      const localHints = getUserStorage('dismissedHints', []);
      setDismissedHints(new Set(localHints));

      const localOnboarding = getUserStorage('hasCompletedOnboarding', null);
      if (localOnboarding !== null) {
        setHasCompletedOnboarding(localOnboarding === true || localOnboarding === 'true');
      }

      // 2. Load from database (authoritative)
      if (AuthService.isAuthenticated()) {
        const response = await fetch(`${API_BASE}/api/onboarding/state`, {
          headers: AuthService.getJsonHeaders()
        });

        if (response.ok) {
          const data = await response.json();

          // Update state with authoritative data
          setHasCompletedOnboarding(data.hasCompletedOnboarding || false);
          setOnboardingStep(data.currentStep || 0);
          setHasHomeLocation(!!data.hasHomeLocation);
          setFollowCount(data.followCount || 0);
          setOnboardingVersion(data.onboardingVersion || null);
          setAccountCreatedAt(parseUtc(data.accountCreatedAt));

          // Merge hints from database
          if (data.dismissedHints && Array.isArray(data.dismissedHints)) {
            const mergedHints = new Set([...localHints, ...data.dismissedHints]);
            setDismissedHints(mergedHints);
            setUserStorage('dismissedHints', [...mergedHints]);
          }

          // Set milestones
          if (data.milestones) {
            setMilestones(prev => ({ ...prev, ...data.milestones }));
          }

          // Sync user-specific localStorage with DB truth
          setUserStorage('hasCompletedOnboarding', data.hasCompletedOnboarding || false);
        }
      } else {
        // No auth token - use localStorage only (guest/not logged in)
        // Set defaults if localStorage didn't provide values (localOnboarding/localTour were null)
        setHasCompletedOnboarding(prev => prev === null ? false : prev);
      }
    } catch (error) {
      console.error('Error loading onboarding state:', error);
      // Fall back to localStorage values already set, default to false if still null
      setHasCompletedOnboarding(prev => prev === null ? false : prev);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // Load state on mount and when auth changes
  // This ensures onboarding state is re-fetched when user logs in (not just on page refresh)
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    const currentUserId = currentUser?.id || null;
    const prevUserId = prevUserIdRef.current;

    // Only re-fetch if user changed (login, logout, or different user)
    if (currentUserId !== prevUserId) {
      prevUserIdRef.current = currentUserId;
      loadOnboardingState();
    }
  }, [currentUser?.id, authLoading, loadOnboardingState]);

  // ============================================================
  // ONBOARDING ACTIONS
  // ============================================================

  /**
   * Mark onboarding as complete
   */
  const completeOnboarding = useCallback(async () => {
    // Optimistic update. The version must move here too, not just on the next
    // /state fetch: the flow calls this and navigates away in the same tick, so
    // a stale version would leave needsFirstRun true and FirstRunGate would
    // bounce the user straight back into the flow they just finished.
    setHasCompletedOnboarding(true);
    setOnboardingVersion(ONBOARDING_VERSION);
    setOnboardingStep(6); // Final step
    setUserStorage('hasCompletedOnboarding', true);

    // Persist to database
    let saved = false;
    try {
      if (AuthService.isAuthenticated()) {
        const response = await fetch(`${API_BASE}/api/onboarding/complete`, {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ version: ONBOARDING_VERSION })
        });
        saved = response.ok;
      }

      // Also update user preferences for backwards compatibility
      await UserPreferencesService.setPreferenceAsync('general', 'completedQuickOnboarding', true);
    } catch (error) {
      console.error('Error persisting onboarding completion:', error);
    }

    // The flow just saved a home location and follows. hasHomeLocation and
    // followCount only come from the server, so without a re-read the Home
    // checklist still said "Follow someone" to a user who had just followed
    // three people, until the next reload. Only after a SAVED completion: if
    // the POST failed, the re-read would bring back the server's old version
    // and the first-run gate would send them through /welcome again.
    if (saved) loadOnboardingState({ silent: true });
  }, [loadOnboardingState]);

  /**
   * Update onboarding step progress
   */
  const updateOnboardingStep = useCallback(async (step) => {
    setOnboardingStep(step);

    try {
      if (AuthService.isAuthenticated()) {
        await fetch(`${API_BASE}/api/onboarding/step`, {
          method: 'PUT',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ step })
        });
      }
    } catch (error) {
      console.error('Error updating onboarding step:', error);
    }
  }, []);

  /**
   * Skip onboarding entirely
   */
  const skipOnboarding = useCallback(async () => {
    // Same-tick version bump as completeOnboarding — a skip has to opt the user
    // out immediately, or the gate re-offers the flow they just declined.
    setHasCompletedOnboarding(true);
    setOnboardingVersion(ONBOARDING_VERSION);
    setUserStorage('hasCompletedOnboarding', true);

    let saved = false;
    try {
      if (AuthService.isAuthenticated()) {
        const response = await fetch(`${API_BASE}/api/onboarding/skip`, {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          // Version matters on skip too — see ONBOARDING_VERSION.
          body: JSON.stringify({ version: ONBOARDING_VERSION })
        });
        saved = response.ok;
      }

      // Backwards compatibility with UserPreferences-based check
      await UserPreferencesService.setPreferenceAsync('general', 'completedQuickOnboarding', true);
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }

    // A skip partway through the flow can still have saved a location or
    // follows (see completeOnboarding, including why only after a saved skip).
    if (saved) loadOnboardingState({ silent: true });
  }, [loadOnboardingState]);

  // ============================================================
  // FEATURE HINT ACTIONS
  // ============================================================

  /**
   * Check if a specific hint has been dismissed
   */
  const hasSeenHint = useCallback((hintKey) => {
    return dismissedHints.has(hintKey);
  }, [dismissedHints]);

  /**
   * Dismiss a feature hint
   */
  const dismissHint = useCallback(async (hintKey, action = 'dismissed') => {
    // Optimistic update
    setDismissedHints(prev => {
      const next = new Set(prev);
      next.add(hintKey);
      setUserStorage('dismissedHints', [...next]);
      return next;
    });

    // Persist to database
    try {
      if (AuthService.isAuthenticated()) {
        await fetch(`${API_BASE}/api/onboarding/hints/${hintKey}`, {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ dismissedAction: action })
        });
      }
    } catch (error) {
      console.error('Error dismissing hint:', error);
    }
  }, []);

  /**
   * Reset all hints (for testing/debugging)
   */
  const resetAllHints = useCallback(async () => {
    // Optimistic update
    setDismissedHints(new Set());
    removeUserStorage('dismissedHints');

    // Persist to database
    try {
      if (AuthService.isAuthenticated()) {
        const response = await fetch(`${API_BASE}/api/onboarding/hints`, {
          method: 'DELETE',
          headers: AuthService.getJsonHeaders()
        });

        if (!response.ok) {
          throw new Error('Failed to clear hints on server');
        }
      }
    } catch (error) {
      console.error('Error clearing hints:', error);
    }
  }, []);

  // ============================================================
  // MILESTONE TRACKING
  // ============================================================

  /**
   * Record a user milestone (first search, first save, etc.)
   */
  const recordMilestone = useCallback(async (milestoneKey) => {
    // Check if already recorded
    if (milestones[milestoneKey]) return;

    // Optimistic update
    setMilestones(prev => ({ ...prev, [milestoneKey]: true }));

    // Persist to database
    try {
      if (AuthService.isAuthenticated()) {
        await fetch(`${API_BASE}/api/onboarding/milestone`, {
          method: 'POST',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify({ milestone: milestoneKey })
        });
      }
    } catch (error) {
      console.error('Error recording milestone:', error);
    }
  }, [milestones]);

  /**
   * Check if user has completed a milestone
   */
  const hasMilestone = useCallback((milestoneKey) => {
    return milestones[milestoneKey] || false;
  }, [milestones]);

  // ============================================================
  // COMPUTED VALUES
  // ============================================================

  /**
   * Determine user's experience level based on milestones
   */
  const getUserLevel = useCallback(() => {
    const completedMilestones = Object.values(milestones).filter(Boolean).length;

    if (completedMilestones === 0) return 'new';
    if (completedMilestones <= 2) return 'beginner';
    if (completedMilestones <= 4) return 'intermediate';
    return 'experienced';
  }, [milestones]);

  /**
   * Check if user should see simplified UI (new users)
   */
  const shouldShowSimplifiedUI = useCallback(() => {
    const level = getUserLevel();
    return level === 'new' || level === 'beginner';
  }, [getUserLevel]);

  /**
   * Check if user needs onboarding (hasn't completed or new version)
   */
  const needsOnboarding = useCallback(() => {
    if (isLoading) return false;
    return !hasCompletedOnboarding;
  }, [isLoading, hasCompletedOnboarding]);

  /**
   * Whether the account is in its first NEW_ACCOUNT_DAYS. Null until known —
   * callers must not read null as "new" (an established account would see
   * new-user surfaces flash in while state loads).
   */
  const isNewAccount = accountCreatedAt === null
    ? null
    : Date.now() - accountCreatedAt < NEW_ACCOUNT_DAYS * 24 * 60 * 60 * 1000;

  /**
   * Whether to send the user through the /welcome first-run flow.
   *
   * Two conditions, and both matter:
   *
   *   The STOP condition is having been through THIS version of the flow.
   *   hasCompletedOnboarding alone won't do: skipOnboarding sets the same flag
   *   the old v1 quiz set, so it can't tell "declined a 5-question quiz in
   *   March" from "went through the three-step flow". Keying on the version
   *   means going through v2 — completing or skipping — opts you out for good,
   *   while a v1-era decision doesn't count against a flow you never saw.
   *
   *   The PULL-IN condition is missing location OR zero follows — the flow only
   *   has something to offer a user who lacks one of them. `||` not `&&`:
   *   someone with a home location but no follows still has a feed with no
   *   artists in it, which is half the point.
   *
   * Null values mean "not loaded yet" and must not read as missing.
   */
  const needsFirstRun = useCallback(() => {
    if (isLoading || hasHomeLocation === null || followCount === null) return false;
    if (onboardingVersion === ONBOARDING_VERSION) return false;
    return !hasHomeLocation || followCount === 0;
  }, [isLoading, onboardingVersion, hasHomeLocation, followCount]);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  const value = {
    // Loading state
    isLoading,

    // Onboarding state
    hasCompletedOnboarding,
    onboardingStep,
    needsOnboarding: needsOnboarding(),

    // First-run flow (/welcome)
    hasHomeLocation,
    followCount,
    onboardingVersion,
    needsFirstRun: needsFirstRun(),
    isNewAccount,

    // Onboarding actions
    completeOnboarding,
    updateOnboardingStep,
    skipOnboarding,

    // Feature hints
    hasSeenHint,
    dismissHint,
    resetAllHints,

    // Milestones
    milestones,
    recordMilestone,
    hasMilestone,

    // Computed values
    userLevel: getUserLevel(),
    shouldShowSimplifiedUI: shouldShowSimplifiedUI(),

    // Refresh
    refresh: loadOnboardingState
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
